import React from 'react';

/**
 * Mira Orb — sparkle-star pattern, aubergine + gold (Xin brand).
 *
 * state = 'idle'      → gentle 3s halo breath; ambient dust twinkle
 *   state = 'thinking'  → dust orbits faster; star shimmer sweep
 *   state = 'speaking'  → star pulses warmly with each token
 *
 * Palette: aubergine core #3A1140 → #531B55, gold star #E8B84B / #F5C96A,
 *          faint green presence ring #2A4238 @ 15% (mother's benediction).
 */
export default function MiraOrb({ state = 'idle', size = 64, ariaLabel = 'Mira' }) {
  const gid = React.useId().replace(/:/g, '');
  const coreGrad = `mo-core-${gid}`;
  const haloGrad = `mo-halo-${gid}`;
  const starGrad = `mo-star-${gid}`;

  return (
    <span
      role="img"
      aria-label={ariaLabel}
      data-testid="mira-orb"
      data-state={state}
      style={{ display: 'inline-block', width: size, height: size, position: 'relative', lineHeight: 0 }}
    >
      <style>{`
        @keyframes mo-breathe { 0%, 100% { transform: scale(0.94); opacity: 0.55; } 50% { transform: scale(1.06); opacity: 0.95; } }
        @keyframes mo-orbit-a { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes mo-orbit-b { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
        @keyframes mo-star-pulse { 0%, 100% { transform: scale(1); filter: drop-shadow(0 0 2px #E8B84B); }
                                    50%      { transform: scale(1.14); filter: drop-shadow(0 0 6px #F5C96A); } }
        @keyframes mo-star-glow  { 0%, 100% { filter: drop-shadow(0 0 3px rgba(232,184,75,0.4)); }
                                    50%      { filter: drop-shadow(0 0 8px rgba(232,184,75,0.8)); } }
        @keyframes mo-twinkle    { 0%, 100% { opacity: 0.35; transform: scale(0.7); }
                                    50%      { opacity: 1;    transform: scale(1);   } }
        .mo-halo-idle     { animation: mo-breathe 3.2s ease-in-out infinite; transform-origin: 50% 50%; }
        .mo-halo-thinking { animation: mo-breathe 1.4s ease-in-out infinite; transform-origin: 50% 50%; }
        .mo-halo-speaking { animation: mo-breathe 1.0s ease-in-out infinite; transform-origin: 50% 50%; }

        .mo-star-idle     { animation: mo-star-glow  3.2s ease-in-out infinite; transform-origin: 50% 50%; }
        .mo-star-thinking { animation: mo-star-glow  0.9s linear infinite;      transform-origin: 50% 50%; }
        .mo-star-speaking { animation: mo-star-pulse 0.7s ease-in-out infinite; transform-origin: 50% 50%; }

        .mo-orbit-a      { animation: mo-orbit-a 8s linear infinite;  transform-origin: 50% 50%; }
        .mo-orbit-a-fast { animation: mo-orbit-a 2.6s linear infinite; transform-origin: 50% 50%; }
        .mo-orbit-b      { animation: mo-orbit-b 12s linear infinite; transform-origin: 50% 50%; }
        .mo-orbit-b-fast { animation: mo-orbit-b 3.4s linear infinite; transform-origin: 50% 50%; }

        .mo-twinkle-1 { animation: mo-twinkle 2.2s ease-in-out infinite;         transform-origin: 50% 50%; }
        .mo-twinkle-2 { animation: mo-twinkle 2.8s ease-in-out 0.6s infinite;    transform-origin: 50% 50%; }
        .mo-twinkle-3 { animation: mo-twinkle 3.4s ease-in-out 1.1s infinite;    transform-origin: 50% 50%; }
      `}</style>
      <svg viewBox="0 0 100 100" width={size} height={size} style={{ overflow: 'visible' }}>
        <defs>
          <radialGradient id={coreGrad} cx="50%" cy="42%" r="60%">
            <stop offset="0%"  stopColor="#7A2F7C" />
            <stop offset="55%" stopColor="#3A1140" />
            <stop offset="100%" stopColor="#26102E" />
          </radialGradient>
          <radialGradient id={haloGrad} cx="50%" cy="50%" r="50%">
            <stop offset="55%" stopColor="rgba(232,184,75,0.0)" />
            <stop offset="82%" stopColor="rgba(232,184,75,0.55)" />
            <stop offset="100%" stopColor="rgba(232,184,75,0.0)" />
          </radialGradient>
          <linearGradient id={starGrad} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"  stopColor="#F5C96A" />
            <stop offset="55%" stopColor="#E8B84B" />
            <stop offset="100%" stopColor="#B7861B" />
          </linearGradient>
        </defs>

        {/* faint green presence — always slow */}
        <circle cx="50" cy="50" r="46" fill="none" stroke="#2A4238" strokeOpacity="0.18" strokeWidth="1.4" />

        {/* gold breathing halo */}
        <g className={`mo-halo-${state}`}>
          <circle cx="50" cy="50" r="42" fill={`url(#${haloGrad})`} />
        </g>

        {/* aubergine core */}
        <circle cx="50" cy="50" r="30" fill={`url(#${coreGrad})`} />
        {/* soft top highlight — mother's smile */}
        <ellipse cx="43" cy="38" rx="10" ry="4.5" fill="rgba(245,201,106,0.28)" />

        {/* orbiting sparkle dust */}
        <g className={state === 'thinking' ? 'mo-orbit-a-fast' : 'mo-orbit-a'}>
          <circle cx="80" cy="50" r="1.4" fill="#F5C96A" className="mo-twinkle-1" />
          <circle cx="50" cy="20" r="1.1" fill="#F5C96A" className="mo-twinkle-2" />
        </g>
        <g className={state === 'thinking' ? 'mo-orbit-b-fast' : 'mo-orbit-b'}>
          <circle cx="20" cy="52" r="1.2" fill="#E8B84B" className="mo-twinkle-3" />
          <circle cx="65" cy="82" r="0.9" fill="#F5C96A" className="mo-twinkle-1" />
        </g>

        {/* Four-point gold sparkle-star — the "spark" of Mira */}
        <g className={`mo-star-${state}`} transform="translate(50 51)">
          <path
            d="M 0 -18 L 4 -4 L 18 0 L 4 4 L 0 18 L -4 4 L -18 0 L -4 -4 Z"
            fill={`url(#${starGrad})`}
            stroke="#B7861B" strokeWidth="0.4" strokeOpacity="0.6"
          />
          {/* inner glow highlight */}
          <path d="M 0 -12 L 2 -2 L 12 0 L 2 2 L 0 12 L -2 2 L -12 0 L -2 -2 Z" fill="rgba(255,235,180,0.45)" />
          {/* tiny center dot */}
          <circle cx="0" cy="0" r="1.6" fill="#FFF3C7" />
        </g>
      </svg>
    </span>
  );
}
